import { Data, Effect, Schema } from "effect";
import { ShippingPolicy } from "../policies/ShippingPolicy";
import { FinancialCalculationService } from "../services/FinancialCalculationService";
import { InvoiceNumberFormatValidator } from "../services/InvoiceNumberFormatValidator";
import { CurrencySchema } from "../value-objects/Currency";
import { CustomerInfoSchema, type CustomerInfo } from "../value-objects/CustomerInfo";
import {
  InvoiceFinancialsSchema,
  validateGrandTotalCalculation,
  type InvoiceFinancials,
} from "../value-objects/InvoiceFinancials";
import { MoneySchema } from "../value-objects/Money";
import { OrderItemSchema, type OrderItem } from "../value-objects/OrderItem";

/**
 * Domain errors for Invoice operations
 */
export class InvoiceValidationError extends Data.TaggedError("@/InvoiceValidationError")<{
  readonly cause?: unknown;
  readonly message: string;
}> {}

export class InvoiceNumberFormatError extends Data.TaggedError("@/InvoiceNumberFormatError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly invoiceNumber: string;
}> {}

export class InvoiceImmutabilityError extends Data.TaggedError("@/InvoiceImmutabilityError")<{
  readonly cause?: unknown;
  readonly message: string;
}> {}

/**
 * Schema for Invoice aggregate root with complete validation
 */
export const InvoiceSchema = Schema.Struct({
  invoiceId: Schema.String.pipe(
    Schema.nonEmptyString(),
    Schema.pattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, {
      message: () => "Invoice ID must be a valid UUID",
    })
  ),
  orderId: Schema.String.pipe(Schema.nonEmptyString()),
  invoiceNumber: Schema.String.pipe(Schema.nonEmptyString()),
  customerSnapshot: CustomerInfoSchema,
  itemsSnapshot: Schema.Array(OrderItemSchema).pipe(
    Schema.minItems(1, {
      message: () => "Invoice must contain at least one item",
    })
  ),
  financials: InvoiceFinancialsSchema,
  createdOn: Schema.DateFromSelf,
  createdBy: Schema.String.pipe(Schema.nonEmptyString()),
  // Use a direct schema for Uint8Array that accepts actual Uint8Array instances
  pdfBytes: Schema.optional(
    Schema.instanceOf(Uint8Array, {
      message: () => "PDF bytes must be a Uint8Array",
    })
  ),
}).pipe(
  Schema.filter(
    invoice => {
      // Validate that financials subtotal matches sum of item line prices
      const calculatedSubtotal = invoice.itemsSnapshot.reduce(
        (sum, item) => sum.add(item.linePrice),
        new MoneySchema({ currency: CurrencySchema.euro, amount: 0 })
      );
      const tolerance = 0.01;
      return Math.abs(invoice.financials.subtotal.subtract(calculatedSubtotal).amount) <= tolerance;
    },
    {
      message: () => "Invoice financials subtotal must match sum of item line prices",
    }
  ),
  Schema.filter(invoice => validateGrandTotalCalculation(invoice.financials), {
    message: () => "Invoice financials must have correct grand total calculation",
  })
);

/**
 * Invoice aggregate root representing an immutable invoice.
 *
 * Key characteristics:
 * - Immutable after creation (all properties readonly)
 * - Contains snapshots of customer and order data at creation time
 * - Enforces all business rules through validation
 * - Contains PDF bytes for the generated invoice document
 */
export type Invoice = Schema.Schema.Type<typeof InvoiceSchema>;

/**
 * Data required to create an Invoice
 */
export interface CreateInvoiceData {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly invoiceNumber: string;
  readonly customerSnapshot: CustomerInfo;
  readonly itemsSnapshot: OrderItem[];
  readonly financials: InvoiceFinancials;
  readonly createdOn: Date;
  readonly createdBy: string;
  readonly pdfBytes?: Uint8Array | undefined;
}

/**
 * Invoice aggregate root class with factory methods and business operations
 */
export class InvoiceAggregate {
  // eslint-disable-next-line no-unused-vars
  private constructor(private readonly _data: Invoice) {}

  /**
   * Factory method to create a new Invoice aggregate with full validation
   */
  static create(
    data: CreateInvoiceData
  ): Effect.Effect<
    InvoiceAggregate,
    InvoiceValidationError,
    FinancialCalculationService | ShippingPolicy | InvoiceNumberFormatValidator
  > {
    return Effect.gen(function* () {
      // Validate the invoice data using the schema
      const validatedInvoice = yield* Schema.decodeUnknown(InvoiceSchema)(data);

      // Validate invoice number format using configurable service
      const invoiceNumberValidator = yield* InvoiceNumberFormatValidator;
      yield* invoiceNumberValidator.validate(validatedInvoice.invoiceNumber);

      // Validate financial stuff.
      const shippingPolicy = yield* ShippingPolicy;
      const financialValidator = yield* FinancialCalculationService;
      // We can discard the result, because an invalid invoice would cause an error
      yield* financialValidator.validateInvoiceFinancialConsistency(
        validatedInvoice.financials,
        shippingPolicy
      );

      return new InvoiceAggregate(validatedInvoice);
    }).pipe(
      Effect.mapError(
        error =>
          new InvoiceValidationError({
            message: `Invoice validation failed: ${error.message}`,
            cause: error,
          })
      )
    );
  }

  /**
   * Factory method to reconstruct an Invoice aggregate from validated data
   * (e.g., when loading from database)
   */
  static fromValidatedData(data: Invoice): InvoiceAggregate {
    return new InvoiceAggregate(data);
  }

  // Immutable getters for all properties
  get invoiceId(): string {
    return this._data.invoiceId;
  }

  get orderId(): string {
    return this._data.orderId;
  }

  get invoiceNumber(): string {
    return this._data.invoiceNumber;
  }

  get customerSnapshot(): CustomerInfo {
    return this._data.customerSnapshot;
  }

  get itemsSnapshot(): readonly OrderItem[] {
    return this._data.itemsSnapshot;
  }

  get financials(): InvoiceFinancials {
    return this._data.financials;
  }

  get createdOn(): Date {
    return this._data.createdOn;
  }

  get createdBy(): string {
    return this._data.createdBy;
  }

  get pdfBytes(): Uint8Array | undefined {
    return this._data.pdfBytes;
  }

  /**
   * Get the complete invoice data (for serialization/persistence)
   */
  get data(): Invoice {
    return this._data;
  }

  /**
   * Business method: Check if invoice belongs to a specific order
   */
  belongsToOrder(orderId: string): boolean {
    return this._data.orderId === orderId;
  }

  /**
   * Business method: Check if invoice has a specific invoice number
   */
  hasInvoiceNumber(invoiceNumber: string): boolean {
    return this._data.invoiceNumber === invoiceNumber;
  }

  /**
   * Business method: Check if invoice was created by a specific user
   */
  wasCreatedBy(userId: string): boolean {
    return this._data.createdBy === userId;
  }

  /**
   * Business method: Check if invoice was created within a date range
   */
  wasCreatedBetween(startDate: Date, endDate: Date): boolean {
    return this._data.createdOn >= startDate && this._data.createdOn <= endDate;
  }

  /**
   * Business method: Check if invoice total is within a range
   */
  hasTotalBetween(minTotal: number, maxTotal: number): boolean {
    return (
      this._data.financials.grandTotal.isMoreNumeric(minTotal) &&
      this._data.financials.grandTotal.isLessNumeric(maxTotal)
    );
  }

  /**
   * Business method: Get invoice age in days
   */
  getAgeInDays(): number {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - this._data.createdOn.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Business method: Check if invoice contains a specific product
   */
  containsProduct(productNumber: string): boolean {
    return this._data.itemsSnapshot.some(item => item.productNumber === productNumber);
  }

  /**
   * Business method: Get total quantity of all items
   */
  getTotalQuantity(): number {
    return this._data.itemsSnapshot.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Business method: Get customer email for notifications
   */
  getCustomerEmail(): string {
    return this._data.customerSnapshot.email;
  }

  /**
   * Business method: Get formatted invoice display name
   */
  getDisplayName(): string {
    return `Invoice ${this._data.invoiceNumber} for Order ${this._data.orderId}`;
  }

  /**
   * Equality comparison based on invoice ID
   */
  equals(other: InvoiceAggregate): boolean {
    return this._data.invoiceId === other._data.invoiceId;
  }

  /**
   * String representation for debugging
   */
  toString(): string {
    return `Invoice(id=${this._data.invoiceId}, number=${this._data.invoiceNumber}, orderId=${this._data.orderId})`;
  }
}

/**
 * Validation function that returns an Effect
 */
export const validateInvoice = (data: unknown) => Schema.decodeUnknown(InvoiceSchema)(data);
